import { Fragment, type ReactElement, useCallback, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import { useTranslation } from "react-i18next"
import { Button, Checkbox, Divider, Form, Header, Icon, List, Message, Modal } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery } from "api"
import { useAppSelector } from "store"
import util from "utils/utils"

import { TooltipButton } from "./UI"

interface IChallengeStageChanger {
  challengeId: string
  trigger: ReactElement
  history: History
  completed: any
  announcements: OpenAPI.Schemas["Announcement"][]
  template: any
  match: {
    params: {
      id: string
    }
  }
  updateStage: (newStage: string, openWithComms?: boolean) => void
}

const ChallengeStageChanger = ({
  challengeId,
  updateStage,
  completed: propCompleted,
  announcements,
  template,
}: IChallengeStageChanger) => {
  const user = useAppSelector((state) => state.user)
  const challengeQuery = useQuery({
    queryKey: ["challenges", challengeId],
    queryFn: asQuery(api.challenges.get, {
      params: [challengeId],
    }),
  })
  const challenge = challengeQuery.data

  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const deadline = challenge?.deadline
  const { t } = useTranslation()

  const [sendCommunications, setSendCommunications] = useState(false)

  const deadlineInPast = useMemo(() => deadline && moment(deadline) < moment(), [deadline])

  const openChallenge = useCallback(() => {
    setConfirmationModalOpen(true)
  }, [])

  const closeChallenge = useCallback(() => {
    updateStage("closed")
  }, [updateStage])

  const noAudience = useMemo(() => !challenge?.audienceSize || challenge?.audienceSize === 0, [challenge?.audienceSize])

  const noIdeaFormat = useMemo(
    () => !challenge?.ownerIdeaTemplate?.body || challenge?.ownerIdeaTemplate.body?.length === 0,
    [challenge?.ownerIdeaTemplate?.body],
  )

  const completed = { ...(propCompleted || {}) }
  if (!propCompleted) {
    completed.audienceCompleted = challenge.audienceSize > 0 ? "done" : false
    completed.templateCompleted = template?.body?.length > 0 ? "done" : false

    const isDone = !!(
      challenge?.description?.length > 0 &&
      challenge?.description !== undefined &&
      challenge?.instructions?.length > 0 &&
      challenge?.instructions !== undefined &&
      challenge.bannerImage &&
      challenge.name
    )

    const isAlmost =
      challenge.description ||
      challenge.instructions ||
      challenge.sector ||
      challenge.video ||
      challenge.coverImage ||
      challenge.bannerImage

    completed.detailsCompleted = isDone ? "done" : isAlmost ? "almost" : false
    completed.announcementCompleted = announcements?.length > 0 ? "done" : false
  }

  return (
    <Fragment>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
      >
        <Modal.Header>Open {t("generic.challenge")} to audience</Modal.Header>
        <Modal.Content>
          {(completed.audienceCompleted !== "done" ||
            completed.templateCompleted !== "done" ||
            completed.announcementCompleted !== "done" ||
            completed.detailsCompleted !== "done") && (
            <>
              <Message
                warning
                content={
                  <>
                    <Icon name="info" style={{ marginRight: 10 }} />
                    {`You have not completed all the recommended sections of the ${t("generic.challenge")}. Please review the ${t("generic.challenge")} settings before opening the ${t("generic.challenge")}.`}
                  </>
                }
              />
              <Divider hidden />
            </>
          )}
          <p>
            You are about to open the {t("generic.challenge")} "{challenge?.name}" to its audience. Please read and
            review the following before proceeding:
          </p>
          {challenge?.audienceSize && challenge?.audienceSize > 0 ? (
            <Header as="h5">
              There are {util.pluralise(challenge.audienceSize, "user", "users")} in the audience of this{" "}
              {t("generic.challenge")}
            </Header>
          ) : null}
          <List bulleted>
            <List.Item>
              Anyone in the {t("generic.challenge")} audience will be able to make {t("generic.ideas")} for this{" "}
              {t("generic.challenge")}.
            </List.Item>
            <List.Item>
              All information that is part of the {t("generic.challenge")} details will be visible to people viewing the{" "}
              {t("generic.challenge")}. This includes any text, images or files in the summary, description and
              attachments. Please verify no sensitive information will be revealed.
            </List.Item>
          </List>
          {util.hasPermission(user, "org.viewDashboard", challenge?.organisation) ? (
            <>
              <Header as="h5">User notifications</Header>
              <Form>
                <Form.Field>
                  <Checkbox
                    radio
                    label={
                      <label htmlFor="without">
                        <span>
                          <b>Open the {t("generic.challenge")} to the audience, but do not send notification emails</b>
                          <br />
                          Users will be able to view and access the {t("generic.challenge")}, but they will not receive
                          any notification prompting them to view the {t("generic.challenge")}.
                        </span>
                      </label>
                    }
                    name="without"
                    value="without"
                    checked={!sendCommunications}
                    onChange={() => setSendCommunications(false)}
                  />
                </Form.Field>
                <Form.Field>
                  <Checkbox
                    radio
                    label={
                      <label htmlFor="with">
                        <span>
                          <b>Open the {t("generic.challenge")} to the audience and send notification emails</b>
                          <br />
                          Emails and notifications will be sent to users with tags that match this{" "}
                          {t("generic.challenge")}. The email will prompt them to view and participate in the{" "}
                          {t("generic.challenge")}.
                        </span>
                      </label>
                    }
                    name="with"
                    value="with"
                    checked={sendCommunications}
                    onChange={() => setSendCommunications(true)}
                  />
                </Form.Field>
              </Form>
            </>
          ) : null}
          {noIdeaFormat || noAudience ? (
            <Message warning>
              <Message.Header style={{ marginBottom: 5 }}>Missing {t("generic.challenge")} information</Message.Header>
              <Message.Content>
                There seems to be some missing information in the {t("generic.challenge")} settings. You can open the{" "}
                {t("generic.challenge")} now, but please be aware that this may cause issues for users. Make sure to
                review this information before opening the {t("generic.challenge")}. The following issues have been
                identified:
                <List bulleted style={{ marginTop: 10 }}>
                  {noAudience ? (
                    <List.Item>
                      <b>There is currently no audience set up for this {t("generic.challenge")}.</b>
                    </List.Item>
                  ) : null}
                  {noIdeaFormat ? (
                    <List.Item>
                      <b>
                        There is currently no {t("generic.idea")} format selected for this {t("generic.challenge")}.
                      </b>
                    </List.Item>
                  ) : null}
                </List>
                <p>Please revisit the settings to finish setting up this {t("generic.challenge")}.</p>
              </Message.Content>
            </Message>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Button
            content="Cancel"
            onClick={() => {
              setSendCommunications(false)
              setConfirmationModalOpen(false)
            }}
          />
          <Button
            primary
            content={`Open ${t("generic.challenge")}`}
            onClick={() => {
              setConfirmationModalOpen(false)
              setSendCommunications(false)
              updateStage("published", sendCommunications)
            }}
          />
        </Modal.Actions>
      </Modal>
      {challenge?.stage === "published" ? (
        <Button size="tiny" secondary content={t("challenge.stages.published.close")} onClick={closeChallenge} />
      ) : !challenge?.stage ? (
        <Button size="tiny" primary content={t("challenge.stages.draft.open")} onClick={openChallenge} />
      ) : challenge?.stage === "closed" ? (
        <TooltipButton
          size="tiny"
          tooltip={
            deadlineInPast
              ? `You cannot open this ${t("generic.challenge")} as it currently has a deadline in the past, and therefore will close as soon as it is opened. Please update the deadline, or remove it, in the ${t("generic.challenge")} settings`
              : null
          }
          disabled={deadlineInPast}
          onClick={() => openChallenge()}
          primary
          content={t("challenge.stages.closed.reOpen")}
        />
      ) : null}
    </Fragment>
  )
}

export default ChallengeStageChanger
